// Firebase 服務包裝器
// 注意：這些變量會通過 window.setFirebaseServices 設置
let firebaseAuth: any = null;
let firebaseDb: any = null;
let firebaseStorage: any = null;

// Firebase 初始化狀態追蹤
let firebaseInitialized = false;
const initListeners: (() => void)[] = [];

// 監聽 Firebase 初始化完成
const checkFirebaseInitialization = () => {
  if (firebaseAuth && firebaseDb && firebaseStorage && !firebaseInitialized) {
    firebaseInitialized = true;
    console.log('✅ Firebase 服務已準備就緒');
    // 通知所有等待的監聽器
    initListeners.forEach(listener => listener());
    initListeners.length = 0; // 清空監聽器陣列
  }
};

// 定期檢查 Firebase 初始化狀態
const initCheckInterval = setInterval(checkFirebaseInitialization, 100);

// 監聽來自 firebase.ts 的服務設置
if (typeof window !== 'undefined') {
  (window as any).setFirebaseServices = (services: any) => {
    firebaseAuth = services.auth;
    firebaseDb = services.db;
    firebaseStorage = services.storage;
    checkFirebaseInitialization();
  };
}

// 創建獲取服務的函數，帶有延遲檢查
export const getAuth = () => {
  if (!firebaseAuth) {
    console.warn('⚠️ Firebase Auth 尚未初始化，請稍後再試');
    return null;
  }
  return firebaseAuth;
};

export const getDb = () => {
  if (!firebaseDb) {
    console.warn('⚠️ Firebase Firestore 尚未初始化，請稍後再試');
    return null;
  }
  return firebaseDb;
};

export const getStorage = () => {
  if (!firebaseStorage) {
    console.warn('⚠️ Firebase Storage 尚未初始化，請稍後再試');
    return null;
  }
  return firebaseStorage;
};

// 等待 Firebase 初始化的函數
export const waitForFirebase = (): Promise<void> => {
  if (firebaseInitialized) {
    return Promise.resolve();
  }
  
  return new Promise((resolve) => {
    initListeners.push(resolve);
  });
};

// ✅ 修復：使用 Proxy 確保 db 總是指向最新的 firebaseDb
// 問題：直接導出 firebaseDb 時它是 null，即使後來被設置也不會更新
// 解決：使用 Proxy 動態獲取最新值

const dbProxy: any = new Proxy({}, {
  get(target, prop) {
    if (!firebaseDb) {
      console.error('❌ Firestore 尚未初始化，請等待 Firebase 初始化完成');
      // 不拋出錯誤，返回 undefined，讓 Firestore 函數自己處理
      return undefined;
    }
    return (firebaseDb as any)[prop];
  }
});

const authProxy: any = new Proxy({}, {
  get(target, prop) {
    if (!firebaseAuth) {
      console.error('❌ Firebase Auth 尚未初始化');
      return undefined;
    }
    return (firebaseAuth as any)[prop];
  }
});

const storageProxy: any = new Proxy({}, {
  get(target, prop) {
    if (!firebaseStorage) {
      console.error('❌ Firebase Storage 尚未初始化');
      return undefined;
    }
    return (firebaseStorage as any)[prop];
  }
});

// 導出 Proxy（用於向後兼容）
export const auth = authProxy;
export const db = dbProxy;
export const storage = storageProxy;

// 導出 Firestore 函數
export { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  deleteDoc, 
  doc, 
  updateDoc, 
  addDoc, 
  arrayUnion, 
  arrayRemove, 
  writeBatch, 
  setDoc, 
  serverTimestamp, 
  limit, 
  getDoc, 
  startAfter 
} from 'firebase/firestore';

// 導出 Auth 函數
export { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';

// 導出 Firestore 函數
import { doc, setDoc, getDoc, updateDoc, increment } from 'firebase/firestore';

// 增加影片關注度
export async function incrementAttention(videoId: string): Promise<void> {
  const db = getDb();
  if (!videoId || !db) {
    console.warn('incrementAttention: videoId 或 db 未定義');
    return;
  }

  try {
    const videoStatsRef = doc(db, 'videoStats', videoId);
    const videoStatsSnap = await getDoc(videoStatsRef);
    
    if (videoStatsSnap.exists()) {
      // 如果文檔存在，增加關注度
      await updateDoc(videoStatsRef, {
        attentionCount: increment(1),
        lastPlayedAt: new Date().toISOString()
      });
    } else {
      // 如果文檔不存在，創建並設定關注度為 1
      await setDoc(videoStatsRef, {
        attentionCount: 1,
        lastPlayedAt: new Date().toISOString(),
        createdAt: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('增加關注度失敗:', error);
  }
}

// 取得影片關注度
export async function getAttentionCount(videoId: string): Promise<number> {
  const db = getDb();
  if (!videoId || !db) {
    return 0;
  }

  try {
    const videoStatsRef = doc(db, 'videoStats', videoId);
    const videoStatsSnap = await getDoc(videoStatsRef);
    
    if (videoStatsSnap.exists()) {
      const data = videoStatsSnap.data();
      return data.attentionCount || 0;
    }
    return 0;
  } catch (error) {
    console.error('取得關注度失敗:', error);
    return 0;
  }
}